// src/App.js
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';

import {
  ThemeProvider,
  createTheme
} from '@mui/material/styles';

import {
  CssBaseline,
  CircularProgress,
  Toolbar
} from '@mui/material';

import { AuthProvider, useAuth } from './contexts/AuthContext';

import Header from './components/Layout/Header';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import RecommendationsPage from './pages/RecommendationsPage';
import MyInvitationsPage from './pages/MyInvitationsPage';
import ProjectsPage from './pages/ProjectsPage';
import CreateProjectPage from './pages/CreateProjectPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProfilePage from './pages/ProfilePage';
import NotificationsPage from './pages/NotificationsPage';
import QuestionnaireAnalyticsPage from './pages/QuestionnaireAnalyticsPage';

import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import SectionDetailPage from './pages/SectionDetailPage';
import CreateEventPage from './pages/CreateEventPage';

import VKCallbackHandler from './pages/VKCallbackHandler';
import VKLandingHandler from './components/VKLandingHandler';

import ModernQuestionnaire from './components/Questionnaires/ModernQuestionnaire';
// Анкета обратной связи
import QuestionnairePage from './pages/QuestionnairePage';

import './App.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#3c6e71',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
});

// Защищённые маршруты для авторизованных пользователей.
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Публичные маршруты для неавторизованных пользователей.
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();
  const isQuestionnairePage = /^\/questionnaire\/[^/]+$/.test(location.pathname);

  return (
    <div className="App">
      {user && <Header />}
      {/* На странице анкеты не добавляем пустой Toolbar: он создавал полосу над карточкой. */}
      {user && !isQuestionnairePage && <Toolbar />}

      <main
        style={{
          minHeight: isQuestionnairePage ? 'calc(100vh - 82px)' : 'calc(100vh - 64px)',
          paddingTop: user && !isQuestionnairePage ? '16px' : '0',
        }}
      >
        <Routes>
          <Route
            path="/"
            element={<VKLandingHandler />}
          />

          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          <Route
            path="/vk-callback"
            element={<VKCallbackHandler />}
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Аналитика анкет */}
          <Route
            path="/questionnaire-analytics"
            element={
              <ProtectedRoute>
                <QuestionnaireAnalyticsPage />
              </ProtectedRoute>
            }
          />

          {/* Анкета научных компетенций — сохраняем существующую форму */}
          <Route
            path="/questionnaire/3"
            element={
              <ProtectedRoute>
                <ModernQuestionnaire />
              </ProtectedRoute>
            }
          />

          {/* Анкета обратной связи и другие анкеты из базы данных */}
          <Route
            path="/questionnaire/:id"
            element={
              <ProtectedRoute>
                <QuestionnairePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/recommendations"
            element={
              <ProtectedRoute>
                <RecommendationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-invitations"
            element={
              <ProtectedRoute>
                <MyInvitationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/create"
            element={
              <ProtectedRoute>
                <CreateProjectPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <ProjectDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <EventsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/events/create"
            element={
              <ProtectedRoute>
                <CreateEventPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/events/:id"
            element={
              <ProtectedRoute>
                <EventDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/events/:eventId/sections/:sectionId"
            element={
              <ProtectedRoute>
                <SectionDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile/:userId?"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              <Navigate
                to={user ? '/dashboard' : '/login'}
                replace
              />
            }
          />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;